import Stack from "@mui/material/Stack";
import type { CSSObject } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import type { TypographyProps } from "@mui/material/Typography";
import Typography from "@mui/material/Typography";
import { type ReactNode } from "react";

interface HeadingContainerProps {
  title: ReactNode;
  variant: TypographyProps["variant"];
  borderType: "solid" | "dashed";
  children?: ReactNode;
  slotProps?: {
    title?: CSSObject;
  };
}

export function HeadingContainer({
  title,
  variant,
  borderType,
  children,
  slotProps,
}: HeadingContainerProps) {
  const theme = useTheme();

  const variantKey = `${variant}${borderType[0].toUpperCase()}${borderType.slice(1)}Divider`;
  const variants = theme.components?.MuiTypography?.variants ?? [];
  const match = variants?.find(
    (v) => typeof v?.props === "object" && v?.props?.variant === variantKey,
  );
  const styles = (
    typeof match?.style === "function" ? match.style({ theme }) : match?.style
  ) as CSSObject | undefined;

  let computedPaddingBottom: number | string = "12px";
  if (styles && typeof styles === "object") {
    if (typeof styles.paddingBottom === "string") {
      computedPaddingBottom = styles.paddingBottom;
    } else if (typeof styles.paddingBottom === "number") {
      computedPaddingBottom = styles.paddingBottom + "px";
    }
  }

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        mb: 1,
        pb: computedPaddingBottom,
        borderBottom: `1px ${borderType}`,
        borderColor: "components.input.outlined.enabledBorder",
      }}
    >
      <Typography
        variant={variant}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          ...(slotProps?.title ?? {}),
        }}
      >
        {title}
      </Typography>
      {children}
    </Stack>
  );
}
